class MiniImageHover extends HTMLElement {
  constructor() {
    super();
    this.maxClones = 2;
    this.cloneCount = 0;
  }

  connectedCallback() {
    this.sectionId = this.id;
    if (!this.sectionId) return;
    this.init();
  }

  isMobile() {
    return window.innerWidth < 768 || /mobile|android|iphone|ipad|phone/i.test(navigator.userAgent);
  }

  init() {
    this.container = this.querySelector('.mini-image-with-hover-mobile');
    this.centerImageList = this.querySelector('.image-list-center');
    this.row1 = this.querySelector('.grid-row.row-1');
    this.row2 = this.querySelector('.grid-row.row-2');

    if (!this.container || !this.row1 || !this.row2 || !this.centerImageList) return;

    this.initObserver();
    this.initEvents();
  }

  initEvents() {
    this.addEventListener(
      'mouseenter',
      (e) => {
        const img = e.target.closest(
          '.mobile-img-wrapper img, .mini-image-with-hover-footer span, .image-list-left img, .image-list-right img'
        );
        if (img) {
          const blockId = img.id || img.dataset?.blockId;
          if (blockId) this.changeImageOnHover(blockId);
        }
      },
      true
    );

    this.addEventListener('click', (e) => {
      const img = e.target.closest('.mobile-img-wrapper img');
      if (img) {
        const blockId = img.id;
        if (blockId) this.changeImageOnHover(blockId);
      }
    });
  }

  changeImageOnHover(blockId) {
    const images = this.centerImageList.querySelectorAll('.image-item');

    images.forEach((image) => {
      if (image.id === `center-${blockId}`) image.classList.remove('hidden-image');
      else image.classList.add('hidden-image');
    });

    if (this.isMobile()) {
      const wrappers = this.querySelectorAll('.mobile-img-wrapper');
      wrappers.forEach((wrapper) => {
        if (wrapper.querySelector(`#${blockId}`)) wrapper.classList.add('active-wrapper');
        else wrapper.classList.remove('active-wrapper');
      });

      const footerSpans = this.querySelectorAll('.mini-image-with-hover-footer span');
      footerSpans.forEach((span) => {
        if (span.dataset.blockId === blockId) span.classList.add('active-footer');
        else span.classList.remove('active-footer');
      });
    }
  }

  initObserver() {
    const observerTarget = document.createElement('div');
    observerTarget.classList.add('observer-target');
    observerTarget.style.width = '1px';
    observerTarget.style.height = '100%';
    observerTarget.style.display = 'inline-block';
    this.row1.appendChild(observerTarget);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.cloneCount < this.maxClones) {
            this.duplicateMobileBlocks(observerTarget);
          }
        });
      },
      {
        root: this.container,
        rootMargin: '0px',
        threshold: 0.01,
      }
    );

    this.observer.observe(observerTarget);
  }

  duplicateMobileBlocks(observerTarget) {
    const row1Clones = this.row1.querySelectorAll('.mobile-img-wrapper');
    const row2Clones = this.row2.querySelectorAll('.mobile-img-wrapper');

    row1Clones.forEach((node) => {
      const clone = node.cloneNode(true);
      this.row1.appendChild(clone);
    });

    row2Clones.forEach((node) => {
      const clone = node.cloneNode(true);
      this.row2.appendChild(clone);
    });

    this.cloneCount++;

    this.observer.unobserve(observerTarget);
    this.row1.appendChild(observerTarget);
    this.observer.observe(observerTarget);
  }
}

if (!customElements.get('mini-image-hover')) {
  customElements.define('mini-image-hover', MiniImageHover);
}


class DualCarousel extends HTMLElement {
  connectedCallback() {
    this.initCarousel('.top-carousel', -1, 0.4);    // right-to-left
    this.initCarousel('.bottom-carousel', 1, 0.4);  // left-to-right
    this.initLoadingAnimation();
  }

  initLoadingAnimation() {
    const section = this.querySelector('.dual-carousel-section');
    if (!section) return;

    // Show after 300ms delay
    setTimeout(() => {
      section.classList.add('dual-carousel-loaded');
    }, 200);
  }

  initCarousel(selector, direction = 1, speed = 0.3) {
    const wrapper = this.querySelector(selector);
    if (!wrapper) return;

    const track = wrapper.querySelector('.carousel-track');
    if (!track) return;

    // Prevent multiple clones
    if (track.dataset.cloned === 'true') return;

    const originalSlides = Array.from(track.children);

    const clones = originalSlides.map(slide => slide.cloneNode(true));

    if (direction === -1) {
      clones.reverse().forEach(clone => {
        track.insertBefore(clone, track.firstChild);
      });
    } else {
      clones.forEach(clone => {
        track.appendChild(clone);
      });
    }

    track.dataset.cloned = 'true';

    let currentX = direction === -1 ? track.scrollWidth / 2 : 0;

    const step = () => {
      currentX += speed * direction;

      if (direction === -1 && currentX <= 0) {
        currentX = track.scrollWidth / 2;
      }

      if (direction === 1 && currentX >= track.scrollWidth / 2) {
        currentX = 0;
      }

      track.style.transform = `translateX(${-currentX}px)`;
      requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }
}

if (!customElements.get('dual-carousel')) {
  customElements.define('dual-carousel', DualCarousel);
}

class DualScrollGallery extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    const sectionId = this.dataset.sectionId;
    this.desktopContainer = this.querySelector('.dsg-desktop');

    // --- DESKTOP LOGIC ---
    if (this.desktopContainer) {
      this.leftTrack = this.querySelector('.dsg-col-left .dsg-scroll-track');
      this.rightTrack = this.querySelector('.dsg-col-right .dsg-scroll-track');
      this.thumbsTrack = this.querySelector('.dsg-thumbs-track');
      this.centerColumn = this.querySelector('.dsg-center');
      this.btnUp = this.querySelector('.dsg-nav-btn:first-child');
      this.btnDown = this.querySelector('.dsg-nav-btn:last-child');

      // Count real items based on data-real-index attribute
      const realItems = this.leftTrack.querySelectorAll('.dsg-item[data-real-index]');
      this.realCount = realItems.length;

      this.cloneHead = 2; // Number of clones at top
      this.cloneTail = 2; // Number of clones at bottom

      // Start index = The first Real Item
      this.startIndex = this.cloneHead;
      this.visualIndex = this.startIndex;
      this.isAnimating = false;

      // Config
      this.thumbH = 150;
      this.thumbGap = 10;
      this.activeBlockH = this.thumbH + this.thumbGap + this.thumbH;
      this.pairStep = (this.thumbH + this.thumbGap) * 2;
      this.maskH = 576;

      this.centerOffset = (this.maskH - this.activeBlockH) / 2;

      this.setPositions(this.startIndex, false);

      this.btnUp.addEventListener('click', () => this.move(-1));
      this.btnDown.addEventListener('click', () => this.move(1));

      this.leftTrack.addEventListener('transitionend', () => this.handleTransitionEnd());

      this.centerColumn.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
    }

    // --- MOBILE LOGIC ---
    this.mobileContainer = this.querySelector('.dsg-mobile-main');
    if (this.mobileContainer) {
      this.initMobile();
    }
  }

  setPositions(index, enableTransition = true) {
    const duration = enableTransition ? '0.6s' : '0s';

    [this.leftTrack, this.rightTrack, this.thumbsTrack].forEach(track => {
      track.style.transition = `transform ${duration} cubic-bezier(0.25, 1, 0.5, 1)`;
    });

    // Main Images
    const translatePct = index * -100;
    this.leftTrack.style.transform = `translateY(${translatePct}%)`;
    this.rightTrack.style.transform = `translateY(${translatePct}%)`;

    // Thumbs
    const finalThumbY = this.centerOffset - (this.visualIndex * this.pairStep);
    this.thumbsTrack.style.transform = `translateY(${finalThumbY}px)`;

    // Active Classes
    // Determine effective real index for highlighting
    let effectiveRealIndex = 0;

    // Map visual index to real index
    if (index < this.cloneHead) {
      // In Top Clones
      effectiveRealIndex = this.realCount - (this.cloneHead - index);
    } else if (index >= this.realCount + this.cloneHead) {
      // In Bottom Clones
      effectiveRealIndex = index - (this.realCount + this.cloneHead);
    } else {
      // Real Items
      effectiveRealIndex = index - this.cloneHead;
    }

    const allThumbs = this.thumbsTrack.querySelectorAll('.dsg-thumb-wrapper');
    allThumbs.forEach(w => {
      const img = w.querySelector('.dsg-thumb');
      if (img) img.classList.remove('active');
    });

    const w1 = allThumbs[this.visualIndex * 2];
    const w2 = allThumbs[this.visualIndex * 2 + 1];
    if (w1) w1.querySelector('.dsg-thumb').classList.add('active');
    if (w2) w2.querySelector('.dsg-thumb').classList.add('active');
  }

  move(direction) {
    if (this.isAnimating) return;
    this.isAnimating = true;

    this.visualIndex += direction;
    this.setPositions(this.visualIndex, true);
  }

  handleTransitionEnd() {
    this.isAnimating = false;

    const realLastIndex = this.startIndex + this.realCount - 1;

    // Scrolled UP into Top Clones
    if (this.visualIndex < this.startIndex) {
      const offsetFromStart = this.startIndex - this.visualIndex;
      this.visualIndex = realLastIndex - (offsetFromStart - 1);
      this.setPositions(this.visualIndex, false);
    }

    // Scrolled DOWN into Bottom Clones
    if (this.visualIndex > realLastIndex) {
      const offsetFromEnd = this.visualIndex - realLastIndex;
      this.visualIndex = this.startIndex + (offsetFromEnd - 1);
      this.setPositions(this.visualIndex, false);
    }
  }

  handleWheel(e) {
    e.preventDefault();
    if (this.isAnimating) return;
    if (Math.abs(e.deltaY) < 15) return;

    if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if (e.deltaY > 0) {
        this.move(1);
      } else {
        this.move(-1);
      }
    }, 30);
  }

  initMobile() {
    this.mobileThumbs = this.querySelectorAll('.dsg-mobile-thumb-img');
    this.mobileItems = this.querySelectorAll('.dsg-mobile-item');
    this.mobileThumbsTrack = this.querySelector('.dsg-mobile-thumbs-track');
    this.mobileThumbsMask = this.querySelector('.dsg-mobile-thumbs-mask');
    this.mobileUp = this.querySelector('.mobile-up');
    this.mobileDown = this.querySelector('.mobile-down');

    if (!this.mobileThumbsTrack) return;

    // Mobile Config
    this.mThumbH = 92;
    this.mThumbGap = 10;
    this.mMaskH = 400;
    this.mVisibleCount = 4;

    // Store original templates for cloning
    this.originalItems = Array.from(this.mobileItems).map(item => item.cloneNode(true));
    this.originalThumbs = Array.from(this.mobileThumbs).map(thumb => thumb.cloneNode(true));

    this.currentMobileIndex = 0;
    this.isSyncing = false;
    this.updateMobileThumbs(0);

    // Click on Thumb -> Scroll Main (Delegation)
    this.addThumbListeners();

    // Observers for Sync & Infinite Scroll
    this.initMobileObservers();

    // Scroll End Listener for Thumbs (Debounced)
    this.mobileThumbsMask.addEventListener('scroll', () => {
      if (this.isSyncing) return;
      if (this.scrollDebounce) clearTimeout(this.scrollDebounce);
      this.scrollDebounce = setTimeout(() => {
        this.handleThumbScrollEnd();
      }, 150);
    }, { passive: true });

    // Arrows
    if (this.mobileUp) {
      this.mobileUp.addEventListener('click', () => {
        const newIndex = Math.max(0, this.currentMobileIndex - 1);
        if (this.mobileItems[newIndex]) {
          this.mobileItems[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }

    if (this.mobileDown) {
      this.mobileDown.addEventListener('click', () => {
        const newIndex = Math.min(this.mobileItems.length - 1, this.currentMobileIndex + 1);
        if (this.mobileItems[newIndex]) {
          this.mobileItems[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }
  }

  addThumbListeners() {
    // Re-select in case of clones
    this.mobileThumbs = this.querySelectorAll('.dsg-mobile-thumb-img');
    this.mobileItems = this.querySelectorAll('.dsg-mobile-item');

    this.mobileThumbs.forEach((thumb, index) => {
      // Remove old listeners to avoid duplicates? 
      // Better to just add to new ones or use delegation.
      // Let's use delegation on the track if possible, but click on specific thumb is easier.
      // For now, just ensure we don't double bind if we call this again.
      // Actually, simpler: just bind to the track and check target.
    });

    // Event Delegation for Thumbs
    // We'll do this once in init, but we need to handle dynamic indices.
    if (!this.thumbsDelegated) {
      this.mobileThumbsTrack.addEventListener('click', (e) => {
        const thumb = e.target.closest('.dsg-mobile-thumb-img');
        if (thumb) {
          // Find index in the current list
          const allThumbs = this.querySelectorAll('.dsg-mobile-thumb-img');
          const index = Array.from(allThumbs).indexOf(thumb);
          if (this.mobileItems[index]) {
            this.mobileItems[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      });
      this.thumbsDelegated = true;
    }
  }

  initMobileObservers() {
    // 1. Main Image Active Observer (Sync Main -> Thumb)
    const mainActiveObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (this.isSyncing) return; // Ignore if syncing from thumb

          const currentItems = this.querySelectorAll('.dsg-mobile-item');
          const idx = Array.from(currentItems).indexOf(entry.target);
          this.updateMobileThumbs(idx);
        }
      });
    }, { threshold: 0.6 });

    this.mobileItems.forEach(item => mainActiveObserver.observe(item));
    this.mainActiveObserver = mainActiveObserver;

    // 2. Main Infinite Scroll Observer
    const mainInfiniteObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.appendClones();
        }
      });
    }, { rootMargin: '200px' });

    if (this.mobileItems.length > 0) {
      const triggerIndex = Math.max(0, this.mobileItems.length - 3);
      mainInfiniteObserver.observe(this.mobileItems[triggerIndex]);
    }
    this.mainInfiniteObserver = mainInfiniteObserver;

    // 3. Thumbnail Infinite Scroll Observer
    const thumbInfiniteObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.appendClones();
        }
      });
    }, {
      root: this.mobileThumbsMask,
      rootMargin: '200px'
    });

    if (this.mobileThumbs.length > 0) {
      const triggerIndex = Math.max(0, this.mobileThumbs.length - 3);
      thumbInfiniteObserver.observe(this.mobileThumbs[triggerIndex]);
    }
    this.thumbInfiniteObserver = thumbInfiniteObserver;
  }

  handleThumbScrollEnd() {
    const scrollTop = this.mobileThumbsMask.scrollTop;
    const itemHeight = this.mThumbH + this.mThumbGap;
    const index = Math.round(scrollTop / itemHeight);

    this.updateMobileMain(index);
  }

  appendClones() {
    // Append a set of original items
    this.originalItems.forEach(item => {
      const clone = item.cloneNode(true);
      this.mobileContainer.appendChild(clone);
      this.mainActiveObserver.observe(clone);
    });

    // Append a set of original thumbs
    this.originalThumbs.forEach(thumb => {
      const clone = thumb.cloneNode(true);
      this.mobileThumbsTrack.appendChild(clone);
    });

    // Update references
    this.mobileItems = this.querySelectorAll('.dsg-mobile-item');
    this.mobileThumbs = this.querySelectorAll('.dsg-mobile-thumb-img');

    // Move Main Infinite Observer
    this.mainInfiniteObserver.disconnect();
    const mainTrigger = Math.max(0, this.mobileItems.length - 3);
    this.mainInfiniteObserver.observe(this.mobileItems[mainTrigger]);

    // Move Thumb Infinite Observer
    this.thumbInfiniteObserver.disconnect();
    const thumbTrigger = Math.max(0, this.mobileThumbs.length - 3);
    this.thumbInfiniteObserver.observe(this.mobileThumbs[thumbTrigger]);
  }

  updateMobileMain(index) {
    if (index < 0 || index === this.currentMobileIndex) return;

    this.isSyncing = true;
    this.currentMobileIndex = index;

    // Update thumbs active state (visual only, no scroll to avoid jitter)
    this.mobileThumbs.forEach(t => t.classList.remove('is-active'));
    if (this.mobileThumbs[index]) {
      this.mobileThumbs[index].classList.add('is-active');
    }

    if (this.mobileItems[index]) {
      this.mobileItems[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Reset sync flag after scroll
    if (this.syncTimeout) clearTimeout(this.syncTimeout);
    this.syncTimeout = setTimeout(() => {
      this.isSyncing = false;
    }, 600);
  }

  updateMobileThumbs(index) {
    if (index < 0) return;
    if (this.currentMobileIndex === index) return;

    this.isSyncing = true;
    this.currentMobileIndex = index;

    // Update Active Class
    this.mobileThumbs.forEach(t => t.classList.remove('is-active'));
    if (this.mobileThumbs[index]) {
      this.mobileThumbs[index].classList.add('is-active');
    }

    // Scroll Thumbs Mask
    const step = this.mThumbH + this.mThumbGap;
    const targetScroll = index * step;

    if (this.mobileThumbsMask) {
      this.mobileThumbsMask.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    }

    // Reset sync flag
    if (this.syncTimeout) clearTimeout(this.syncTimeout);
    this.syncTimeout = setTimeout(() => {
      this.isSyncing = false;
    }, 600);
  }
}

if (!customElements.get('dual-scroll-gallery')) {
  customElements.define('dual-scroll-gallery', DualScrollGallery);
}

(function() {
  'use strict';
  
  function updateHeaderHeight() {
    const header = document.getElementById('shopify-section-header');
    const headerHeight = header ? header.offsetHeight : 0;
    document.documentElement.style.setProperty('--header-height--component', `${headerHeight}px`);
  }

  const updateAnnouncementBarHeight = () => {
    const announcementBar = document.querySelector('.section-announcement-bar');
    const announcementBarHeight = announcementBar ? announcementBar.offsetHeight : 0;
    document.documentElement.style.setProperty('--announcement-bar-height--component', `${announcementBarHeight}px`);
  };

  function initHeaderHeight() {
    updateHeaderHeight();
    updateAnnouncementBarHeight();
    
    requestAnimationFrame(function() {
      updateHeaderHeight();
      updateAnnouncementBarHeight();
      requestAnimationFrame(updateHeaderHeight);
      requestAnimationFrame(updateAnnouncementBarHeight);
    });
  }
  initHeaderHeight();
})();